import { useState, useMemo, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { TypographyH1, TypographyMuted } from '@/components/ui/typography'
import { Input } from '@/components/ui/input'
import Button from '@/components/Button'
import ButtonWithTooltip from '@/components/ButtonWithTooltip'
import LoadingScreen from '@/pages/LoadingScreen'
import { useMarketResearchByIdQuery } from '@/queries/useMarketResearchByIdQuery'
import { useResearchCoverageProductsNotSearchedQuery } from '@/queries/useResearchCoverageProductsNotSearched'
import { ProductsNotSearched } from '@/types/MarketResearch'
import AddProductMarketResearchDialog from '../Details/components/AddProductMarketResearchDialog'
import useDisclosure from '@/hooks/useDisclosure'
import {
  ChevronDown,
  ChevronRight,
  CircleArrowLeft,
  Layers,
  Loader2,
  Package,
  PlusCircle,
  RefreshCw,
  Search,
  Sparkles,
  Store,
  Tag,
  User,
  X,
} from 'lucide-react'
import { formatDateToString } from '@/utils/date'

import { useBaseStore } from '@/store'
import type { Store as AppStore } from '@/store/type'

interface SelectedProductModalState {
  skCliente?: number
  skProduto?: string
  clientName?: string
  productName?: string
}

const stateSelector = (state: AppStore) => ({
  profile: state.authSlice.state.profile,
})

export default function ProductsNotSearchedPage() {
  const { profile } = useBaseStore(stateSelector)
  const isRepresentative = profile === 'REPRESENTANTE'

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const skPesquisa = Number(searchParams.get('search'))

  useEffect(() => {
    if (!skPesquisa) {
      navigate('/market-research')
    }
  }, [skPesquisa, navigate])

  // Queries
  const { data: dataResearch, isLoading: isLoadingResearch } = useMarketResearchByIdQuery(skPesquisa)
  const marketResearch = dataResearch?.[0]

  const {
    data: notSearchedData,
    isLoading: isLoadingNotSearched,
    refetch,
    isFetching,
  } = useResearchCoverageProductsNotSearchedQuery(skPesquisa)

  // Filters State
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedRevenda, setSelectedRevenda] = useState<string>('ALL')
  const [selectedCliente, setSelectedCliente] = useState<string>('ALL')
  const [selectedEmbalagem, setSelectedEmbalagem] = useState<string>('ALL')

  // Collapsed sections states (store keys of collapsed nodes)
  const [collapsedRevendas, setCollapsedRevendas] = useState<Record<string, boolean>>({})
  const [collapsedClientes, setCollapsedClientes] = useState<Record<string, boolean>>({})
  const [collapsedEmbalagens, setCollapsedEmbalagens] = useState<Record<string, boolean>>({})

  // Modal State
  const {
    isOpen: isAddModalOpen,
    onOpen: onOpenAddModal,
    onClose: onCloseAddModal,
  } = useDisclosure()

  const [selectedProductForModal, setSelectedProductForModal] = useState<SelectedProductModalState>({})

  const handleOpenAddProduct = (item: ProductsNotSearched) => {
    setSelectedProductForModal({
      skCliente: item.SK_CLIENTE,
      skProduto: item.SK_PRODUTO,
      clientName: item.DS_NOME_FANTASIA,
      productName: item.DS_PRODUTO,
    })
    onOpenAddModal()
  }

  // Filter options
  const revendaOptions = useMemo(() => {
    if (!notSearchedData) return []
    const unique = Array.from(new Set(notSearchedData.map((i) => i.DS_REVENDA).filter(Boolean)))
    return unique.sort()
  }, [notSearchedData])

  const clienteOptions = useMemo(() => {
    if (!notSearchedData) return []
    let list = notSearchedData
    if (selectedRevenda !== 'ALL') {
      list = list.filter((i) => i.DS_REVENDA === selectedRevenda)
    }
    const unique = Array.from(new Set(list.map((i) => i.DS_NOME_FANTASIA).filter(Boolean)))
    return unique.sort()
  }, [notSearchedData, selectedRevenda])

  const embalagemOptions = useMemo(() => {
    if (!notSearchedData) return []
    let list = notSearchedData
    if (selectedRevenda !== 'ALL') {
      list = list.filter((i) => i.DS_REVENDA === selectedRevenda)
    }
    if (selectedCliente !== 'ALL') {
      list = list.filter((i) => i.DS_NOME_FANTASIA === selectedCliente)
    }
    const unique = Array.from(new Set(list.map((i) => i.DD_SKU || 'Geral').filter(Boolean)))
    return unique.sort()
  }, [notSearchedData, selectedRevenda, selectedCliente])

  // Filtered flat list
  const filteredData = useMemo(() => {
    if (!notSearchedData) return []

    return notSearchedData.filter((item) => {
      const matchSearch =
        !searchTerm.trim() ||
        item.DS_PRODUTO?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.DS_NOME_FANTASIA?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.DS_REVENDA?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.DD_SKU && item.DD_SKU.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.DD_MARCA && item.DD_MARCA.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (item.NM_FUNCIONARIO && item.NM_FUNCIONARIO.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchRevenda = selectedRevenda === 'ALL' || item.DS_REVENDA === selectedRevenda
      const matchCliente = selectedCliente === 'ALL' || item.DS_NOME_FANTASIA === selectedCliente
      const matchEmbalagem =
        selectedEmbalagem === 'ALL' || (item.DD_SKU || 'Geral') === selectedEmbalagem

      return matchSearch && matchRevenda && matchCliente && matchEmbalagem
    })
  }, [notSearchedData, searchTerm, selectedRevenda, selectedCliente, selectedEmbalagem])

  // Hierarchical Grouping Tree: Revenda -> Cliente -> Embalagem -> Products[]
  const hierarchicalData = useMemo(() => {
    interface EmbalagemGroup {
      embalagemName: string
      products: ProductsNotSearched[]
    }

    interface ClienteGroup {
      clienteName: string
      skCliente?: number
      funcionario?: string
      embalagens: EmbalagemGroup[]
      totalProducts: number
    }

    interface RevendaGroup {
      revendaName: string
      skRevenda?: number
      clientes: ClienteGroup[]
      totalProducts: number
    }

    const revendaMap = new Map<string, Map<string, Map<string, ProductsNotSearched[]>>>()
    const revendaMeta = new Map<string, { skRevenda?: number }>()
    const clienteMeta = new Map<string, { skCliente?: number; funcionario?: string }>()

    filteredData.forEach((item) => {
      const revenda = item.DS_REVENDA || 'Sem Revenda'
      const cliente = item.DS_NOME_FANTASIA || 'Sem Cliente'
      const embalagem = item.DD_SKU || 'Geral'

      if (!revendaMap.has(revenda)) {
        revendaMap.set(revenda, new Map())
        revendaMeta.set(revenda, { skRevenda: item.SK_REVENDA })
      }

      const clientMap = revendaMap.get(revenda)!
      if (!clientMap.has(cliente)) {
        clientMap.set(cliente, new Map())
        clienteMeta.set(`${revenda}__${cliente}`, {
          skCliente: item.SK_CLIENTE,
          funcionario: item.NM_FUNCIONARIO,
        })
      }

      const embalagemMap = clientMap.get(cliente)!
      if (!embalagemMap.has(embalagem)) {
        embalagemMap.set(embalagem, [])
      }

      embalagemMap.get(embalagem)!.push(item)
    })

    const result: RevendaGroup[] = []

    revendaMap.forEach((clientMap, revendaName) => {
      const clientesList: ClienteGroup[] = []
      let revendaTotal = 0

      clientMap.forEach((embalagemMap, clienteName) => {
        const embalagensList: EmbalagemGroup[] = []
        let clienteTotal = 0

        embalagemMap.forEach((products, embalagemName) => {
          embalagensList.push({
            embalagemName,
            products,
          })
          clienteTotal += products.length
        })

        const meta = clienteMeta.get(`${revendaName}__${clienteName}`)
        clientesList.push({
          clienteName,
          skCliente: meta?.skCliente,
          funcionario: meta?.funcionario,
          embalagens: embalagensList,
          totalProducts: clienteTotal,
        })

        revendaTotal += clienteTotal
      })

      const rMeta = revendaMeta.get(revendaName)
      result.push({
        revendaName,
        skRevenda: rMeta?.skRevenda,
        clientes: clientesList,
        totalProducts: revendaTotal,
      })
    })

    return result
  }, [filteredData])

  // KPIs
  const totalRevendas = hierarchicalData.length
  const totalClientes = useMemo(() => {
    let count = 0
    hierarchicalData.forEach((r) => {
      count += r.clientes.length
    })
    return count
  }, [hierarchicalData])

  const totalEmbalagens = useMemo(() => {
    const embalagemSet = new Set<string>()
    filteredData.forEach((i) => embalagemSet.add(i.DD_SKU || 'Geral'))
    return embalagemSet.size
  }, [filteredData])

  const totalProdutosPendentes = filteredData.length

  // Toggle handlers
  const toggleRevenda = (revendaName: string) => {
    setCollapsedRevendas((prev) => ({
      ...prev,
      [revendaName]: !prev[revendaName],
    }))
  }

  const toggleCliente = (key: string) => {
    setCollapsedClientes((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const toggleEmbalagem = (key: string) => {
    setCollapsedEmbalagens((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const expandAll = () => {
    setCollapsedRevendas({})
    setCollapsedClientes({})
    setCollapsedEmbalagens({})
  }

  const collapseAll = () => {
    const rCollapse: Record<string, boolean> = {}
    const cCollapse: Record<string, boolean> = {}
    const eCollapse: Record<string, boolean> = {}

    hierarchicalData.forEach((r) => {
      rCollapse[r.revendaName] = true
      r.clientes.forEach((c) => {
        const cKey = `${r.revendaName}__${c.clienteName}`
        cCollapse[cKey] = true
        c.embalagens.forEach((e) => {
          const eKey = `${cKey}__${e.embalagemName}`
          eCollapse[eKey] = true
        })
      })
    })

    setCollapsedRevendas(rCollapse)
    setCollapsedClientes(cCollapse)
    setCollapsedEmbalagens(eCollapse)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedRevenda('ALL')
    setSelectedCliente('ALL')
    setSelectedEmbalagem('ALL')
  }

  const hasActiveFilters =
    Boolean(searchTerm.trim()) ||
    selectedRevenda !== 'ALL' ||
    selectedCliente !== 'ALL' ||
    selectedEmbalagem !== 'ALL'

  if (isLoadingResearch) return <LoadingScreen />

  return (
    <div className="flex flex-col h-full overflow-auto p-4 bg-slate-50 min-h-screen">
      {/* Breadcrumb */}
      <Breadcrumb className="mb-3">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link className="hover:underline" to="/market-research">
                Planejamento de Pesquisa
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link
                className="hover:underline"
                to={`/market-research/details?search=${skPesquisa}`}
              >
                #{skPesquisa} {marketResearch?.NM_PESQUISA || 'Detalhes'}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold text-slate-800">
              Produtos Não Pesquisados
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header Banner */}
      <div className="bg-white rounded-2xl p-5 shadow-sm border border-slate-200 mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div className="flex items-center gap-3">
          <div className="flex justify-center items-center w-12 h-12 rounded-xl bg-amber-500/10 text-amber-600">
            <Package className="w-6 h-6" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <TypographyH1 className="text-2xl font-bold text-slate-900">
                Produtos Não Pesquisados
              </TypographyH1>
              <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                {totalProdutosPendentes} pendentes
              </span>
            </div>
            <TypographyMuted className="text-xs text-slate-500 mt-0.5">
              {marketResearch?.NM_PESQUISA} &bull; {marketResearch?.DS_REVENDA}{' '}
              {marketResearch?.SK_TEMPO_INICIO && (
                <>
                  &bull; {formatDateToString(marketResearch.SK_TEMPO_INICIO)} a{' '}
                  {formatDateToString(marketResearch.SK_TEMPO_FIM)}
                </>
              )}
            </TypographyMuted>
          </div>
        </div>

        <div className="flex items-center gap-2 w-full md:w-auto justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(`/market-research/details?search=${skPesquisa}`)}
            className="flex items-center gap-1.5 text-xs text-slate-700 bg-white"
          >
            <CircleArrowLeft size={16} />
            Voltar
          </Button>

          <ButtonWithTooltip
            tooltipContent={<p className="text-white">Atualizar lista</p>}
            variant="ghost"
            isLoading={isFetching}
            onClick={() => refetch()}
            className="shadow-sm bg-white border border-slate-200"
          >
            <RefreshCw size={16} />
          </ButtonWithTooltip>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center shrink-0">
            <Store size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Revendas</p>
            <p className="text-xl font-bold text-slate-900">{totalRevendas}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 text-emerald-600 flex items-center justify-center shrink-0">
            <User size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Clientes (PDVs)</p>
            <p className="text-xl font-bold text-slate-900">{totalClientes}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-purple-50 text-purple-600 flex items-center justify-center shrink-0">
            <Layers size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Embalagens</p>
            <p className="text-xl font-bold text-slate-900">{totalEmbalagens}</p>
          </div>
        </div>

        <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-600 flex items-center justify-center shrink-0">
            <Package size={20} />
          </div>
          <div>
            <p className="text-xs text-slate-500 font-medium">Total de Produtos</p>
            <p className="text-xl font-bold text-amber-700">{totalProdutosPendentes}</p>
          </div>
        </div>
      </div>

      {/* Search and Filters Card */}
      <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm mb-4">
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
          {/* Search Box */}
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
            <Input
              placeholder="Buscar por produto, cliente, revenda ou marca..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-8 text-xs h-9 bg-slate-50 border-slate-200 focus:bg-white"
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Filter Selects */}
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            {/* Revenda Select */}
            {revendaOptions.length > 1 && (
              <select
                value={selectedRevenda}
                onChange={(e) => {
                  setSelectedRevenda(e.target.value)
                  setSelectedCliente('ALL')
                  setSelectedEmbalagem('ALL')
                }}
                className="text-xs h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary"
              >
                <option value="ALL">Todas as Revendas</option>
                {revendaOptions.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            )}

            {/* Cliente Select */}
            <select
              value={selectedCliente}
              onChange={(e) => {
                setSelectedCliente(e.target.value)
                setSelectedEmbalagem('ALL')
              }}
              className="text-xs h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary max-w-[200px] truncate"
            >
              <option value="ALL">Todos os Clientes</option>
              {clienteOptions.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>

            {/* Embalagem Select */}
            <select
              value={selectedEmbalagem}
              onChange={(e) => setSelectedEmbalagem(e.target.value)}
              className="text-xs h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="ALL">Todas as Embalagens</option>
              {embalagemOptions.map((emb) => (
                <option key={emb} value={emb}>
                  {emb}
                </option>
              ))}
            </select>

            {hasActiveFilters && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilters}
                className="text-xs text-rose-600 hover:text-rose-700 hover:bg-rose-50 h-9 px-2 gap-1"
              >
                <X size={14} />
                Limpar
              </Button>
            )}

            <div className="h-5 w-px bg-slate-200 hidden md:block mx-1" />

            {/* Expand / Collapse All */}
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={expandAll}
                className="text-[11px] h-9 px-2.5 text-slate-600 bg-white"
              >
                Expandir tudo
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={collapseAll}
                className="text-[11px] h-9 px-2.5 text-slate-600 bg-white"
              >
                Recolher tudo
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      {isLoadingNotSearched ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200">
          <Loader2 className="h-9 w-9 animate-spin text-primary mb-3" />
          <p className="text-sm font-medium text-slate-700">Carregando produtos não pesquisados...</p>
          <TypographyMuted className="text-xs mt-1">Organizando estrutura hierárquica</TypographyMuted>
        </div>
      ) : hierarchicalData.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-2xl border border-slate-200 text-center px-4">
          <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4 shadow-sm">
            <Sparkles size={28} />
          </div>
          <p className="text-lg font-bold text-slate-800">
            {hasActiveFilters ? 'Nenhum produto encontrado com os filtros aplicados' : 'Tudo em dia!'}
          </p>
          <TypographyMuted className="text-sm max-w-md mt-1 mb-4">
            {hasActiveFilters
              ? 'Tente remover os filtros ou buscar por outro termo para visualizar os produtos.'
              : 'Todos os produtos desta pesquisa já foram pesquisados pelos clientes.'}
          </TypographyMuted>
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters} className="text-xs">
              Limpar Filtros
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {hierarchicalData.map((revenda) => {
            const isRevendaCollapsed = Boolean(collapsedRevendas[revenda.revendaName])

            return (
              <div
                key={revenda.revendaName}
                className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden transition-all duration-200"
              >
                {/* Level 1: Revenda Header */}
                <div
                  onClick={() => toggleRevenda(revenda.revendaName)}
                  className="p-4 bg-gradient-to-r from-slate-900 to-slate-800 text-white flex items-center justify-between cursor-pointer select-none hover:from-slate-800 hover:to-slate-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <button className="p-1 rounded hover:bg-white/10 transition-colors text-slate-300">
                      {isRevendaCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-amber-400 shrink-0">
                      <Store size={18} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                          Revenda
                        </span>
                      </div>
                      <h2 className="text-base font-bold text-white leading-tight">
                        {revenda.revendaName}
                      </h2>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-white/10 text-white border border-white/15">
                      {revenda.clientes.length} {revenda.clientes.length === 1 ? 'cliente' : 'clientes'}
                    </span>
                    <span className="px-3 py-1 rounded-full text-xs font-bold bg-amber-400 text-slate-950">
                      {revenda.totalProducts} {revenda.totalProducts === 1 ? 'produto pendente' : 'produtos pendentes'}
                    </span>
                  </div>
                </div>

                {/* Level 1 Content: List of Clients */}
                {!isRevendaCollapsed && (
                  <div className="p-4 space-y-4 bg-slate-50/60">
                    {revenda.clientes.map((cliente) => {
                      const clientKey = `${revenda.revendaName}__${cliente.clienteName}`
                      const isClienteCollapsed = Boolean(collapsedClientes[clientKey])

                      return (
                        <div
                          key={clientKey}
                          className="bg-white rounded-xl border border-slate-200/80 shadow-xs overflow-hidden"
                        >
                          {/* Level 2: Cliente Header */}
                          <div
                            onClick={() => toggleCliente(clientKey)}
                            className="p-3.5 bg-slate-100/70 border-b border-slate-200 flex items-center justify-between cursor-pointer select-none hover:bg-slate-200/60 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <button className="p-0.5 text-slate-500 hover:text-slate-800">
                                {isClienteCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
                              </button>
                              <div className="w-7 h-7 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                                <User size={15} />
                              </div>
                              <div>
                                <h3 className="text-sm font-bold text-slate-900 leading-tight">
                                  {cliente.clienteName}
                                </h3>
                                {cliente.funcionario && (
                                  <p className="text-xs text-slate-500 mt-0.5">
                                    Vendedor: <span className="font-medium text-slate-700">{cliente.funcionario}</span>
                                  </p>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-2">
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                                {cliente.embalagens.length} {cliente.embalagens.length === 1 ? 'embalagem' : 'embalagens'}
                              </span>
                              <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200">
                                {cliente.totalProducts} {cliente.totalProducts === 1 ? 'item' : 'itens'}
                              </span>
                            </div>
                          </div>

                          {/* Level 2 Content: Embalagens inside Cliente */}
                          {!isClienteCollapsed && (
                            <div className="p-3.5 space-y-3.5">
                              {cliente.embalagens.map((embalagem) => {
                                const embKey = `${clientKey}__${embalagem.embalagemName}`
                                const isEmbCollapsed = Boolean(collapsedEmbalagens[embKey])

                                return (
                                  <div
                                    key={embKey}
                                    className="border border-slate-200 rounded-lg overflow-hidden bg-slate-50/40"
                                  >
                                    {/* Level 3: Embalagem Header */}
                                    <div
                                      onClick={() => toggleEmbalagem(embKey)}
                                      className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between cursor-pointer select-none hover:bg-slate-100/80 transition-colors"
                                    >
                                      <div className="flex items-center gap-2">
                                        <button className="p-0.5 text-slate-400 hover:text-slate-700">
                                          {isEmbCollapsed ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                                        </button>
                                        <Package size={14} className="text-purple-600" />
                                        <span className="text-xs font-bold text-slate-800 uppercase tracking-wide">
                                          Embalagem: {embalagem.embalagemName}
                                        </span>
                                      </div>

                                      <span className="text-xs font-semibold text-slate-500 bg-white px-2 py-0.5 rounded border border-slate-200">
                                        {embalagem.products.length} {embalagem.products.length === 1 ? 'produto' : 'produtos'}
                                      </span>
                                    </div>

                                    {/* Level 4: Products Grid */}
                                    {!isEmbCollapsed && (
                                      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                                        {embalagem.products.map((prod, idx) => (
                                          <div
                                            key={`${prod.SK_PRODUTO || idx}_${idx}`}
                                            onClick={() => {
                                              if (isRepresentative) {
                                                handleOpenAddProduct(prod)
                                              }
                                            }}
                                            className={`group relative bg-white border border-slate-200 rounded-lg p-3 transition-all duration-200 flex flex-col justify-between ${
                                              isRepresentative
                                                ? 'hover:border-primary/50 hover:shadow-md cursor-pointer'
                                                : ''
                                            }`}
                                          >
                                            <div>
                                              <div className="flex items-start justify-between gap-2 mb-1.5">
                                                <h4
                                                  className={`text-xs font-bold text-slate-900 line-clamp-2 ${
                                                    isRepresentative ? 'group-hover:text-primary transition-colors' : ''
                                                  }`}
                                                  title={prod.DS_PRODUTO}
                                                >
                                                  {prod.DS_PRODUTO}
                                                </h4>
                                                <span className="shrink-0 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                                                  Pendente
                                                </span>
                                              </div>

                                              <div className="flex flex-wrap gap-1.5 my-2">
                                                {prod.DD_MARCA && (
                                                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 border border-slate-200/60">
                                                    <Tag size={10} className="text-slate-400" />
                                                    {prod.DD_MARCA}
                                                  </span>
                                                )}
                                                {prod.DD_UNIDADE_VENDA && (
                                                  <span className="text-[11px] font-medium px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 border border-blue-100">
                                                    {prod.DD_UNIDADE_VENDA}
                                                  </span>
                                                )}
                                                {prod.SK_PRODUTO && (
                                                  <span className="text-[10px] font-mono text-slate-400">
                                                    #{prod.SK_PRODUTO}
                                                  </span>
                                                )}
                                              </div>
                                            </div>

                                            {/* Action Button CTA - only for REPRESENTANTE */}
                                            {isRepresentative && (
                                              <div className="pt-2 border-t border-slate-100 mt-2 flex items-center justify-between">
                                                <span className="text-[11px] text-slate-500 group-hover:text-slate-700">
                                                  Clique para preencher
                                                </span>
                                                <Button
                                                  size="sm"
                                                  variant="default"
                                                  onClick={(e) => {
                                                    e.stopPropagation()
                                                    handleOpenAddProduct(prod)
                                                  }}
                                                  className="h-7 px-2.5 text-xs font-semibold gap-1 bg-primary group-hover:bg-primary/90 text-white shadow-xs"
                                                >
                                                  <PlusCircle size={13} />
                                                  Informar
                                                </Button>
                                              </div>
                                            )}
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    })}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Add Product Modal */}
      {marketResearch && (
        <AddProductMarketResearchDialog
          isOpen={isAddModalOpen}
          onClose={() => {
            onCloseAddModal()
            setSelectedProductForModal({})
          }}
          MarketResearch={marketResearch}
          initialSkCliente={selectedProductForModal.skCliente}
          initialSkProduto={selectedProductForModal.skProduto}
        />
      )}
    </div>
  )
}
